import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Common from "../../based/Common";
import { IMAGEABLE_TYPE, NOTIFY } from "../../based/Constants";
import CommonModal from "../../based/Modal";
import { Notify } from "../../based/Notify";
import Request from "../../based/Request";
import Textbox from "../../based/inputs/Textbox";
import useFormValidate from "../../hooks/useFormValidate";
import EmbroideryServices from "../../based/services/EmbroideryServices";

const DEFAULT_SIZES = [50, 80, 120, 150, 200];

const initPattern = {
    name: "",
    tag: "",
    ebmUrl: "",
    ebmFileName: "",
};

function _initSizeRows() {
    return DEFAULT_SIZES.map((sizeMm) => ({ sizeMm, dstUrl: "", dstFileName: "", pngUrl: "", pngFileName: "" }));
}

function PatternUploadModal(props) {
    const [pattern, setPattern] = useState({ ...initPattern });
    const [sizeRows, setSizeRows] = useState(_initSizeRows());
    const [isBusy, setIsBusy] = useState(false);
    const { addRef, isValid } = useFormValidate();

    useEffect(() => {
        if (props.id > 0) _getData(props.id);
        else {
            setPattern({ ...initPattern });
            setSizeRows(_initSizeRows());
        }
    }, [props.id]);

    async function _getData(id) {
        setIsBusy(true);
        let [err, data] = await EmbroideryServices.GetDesignFileById(id);
        if (!err && data) {
            setPattern({ ...initPattern, name: data.label || "" });
            let rows = _initSizeRows();
            let row = rows.find((x) => x.sizeMm == data.boundingWidthMm);
            if (row) {
                if (data.fileFormat == "dst") {
                    row.dstUrl = data.fileUrl;
                    row.dstFileName = data.fileName;
                } else if (data.fileFormat == "png") {
                    row.pngUrl = data.fileUrl;
                    row.pngFileName = data.fileName;
                }
            }
            setSizeRows(rows);
        }
        setIsBusy(false);
    }

    async function _handleUploadEbm(e) {
        let file = e.target.files[0];
        if (!file) return;
        setIsBusy(true);
        let res = await Request.UploadDocument(file);
        setIsBusy(false);
        if (res && res.data) {
            setPattern({ ...pattern, ebmUrl: res.data.url, ebmFileName: file.name });
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file .EBM thất bại");
        }
    }

    async function _handleUploadDst(idx, e) {
        let file = e.target.files[0];
        if (!file) return;
        setIsBusy(true);
        let res = await Request.UploadDocument(file);
        setIsBusy(false);
        if (res && res.data) {
            let rows = [...sizeRows];
            rows[idx] = { ...rows[idx], dstUrl: res.data.url, dstFileName: file.name };
            setSizeRows(rows);
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file .DST thất bại");
        }
    }

    async function _handleUploadPng(idx, e) {
        let file = e.target.files[0];
        if (!file) return;
        setIsBusy(true);
        let res = await Request.UploadImage(file, IMAGEABLE_TYPE.StockPhoto);
        setIsBusy(false);
        if (res && res.data && res.data.imageUrl) {
            let rows = [...sizeRows];
            rows[idx] = { ...rows[idx], pngUrl: res.data.imageUrl, pngFileName: file.name };
            setSizeRows(rows);
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file .PNG thất bại");
        }
    }

    function _handleRemoveRow(idx) {
        let rows = [...sizeRows];
        rows[idx] = { ...rows[idx], dstUrl: "", dstFileName: "", pngUrl: "", pngFileName: "" };
        setSizeRows(rows);
    }

    // Lưu ý: EMB_DesignFileDTO hiện tại chỉ lưu 1 file/record, không có field Tên/Tag/EBM gốc ở cấp pattern.
    // Tạm map: mỗi cặp DST/PNG theo size -> 2 record riêng (FileFormat=dst, FileFormat=png),
    // EBM gốc -> 1 record FileFormat=ebm. DesignSizeId tạm dùng giá trị mm vì chưa có bảng EMB_Pattern cha.
    async function _handleSave() {
        if (!isValid()) return;
        setIsBusy(true);
        let hasError = false;

        if (pattern.ebmUrl) {
            let [err] = await EmbroideryServices.CreateUpdateDesignFile({
                id: 0,
                designId: 0,
                designSizeId: 0,
                fileFormat: "ebm",
                fileUrl: pattern.ebmUrl,
                fileName: pattern.ebmFileName,
                label: pattern.name,
            });
            if (err) hasError = true;
        }

        for (let row of sizeRows) {
            if (row.dstUrl) {
                let [err] = await EmbroideryServices.CreateUpdateDesignFile({
                    id: 0,
                    designId: 0,
                    designSizeId: row.sizeMm,
                    fileFormat: "dst",
                    fileUrl: row.dstUrl,
                    fileName: row.dstFileName,
                    label: pattern.name,
                    boundingWidthMm: row.sizeMm,
                });
                if (err) hasError = true;
            }
            if (row.pngUrl) {
                let [err] = await EmbroideryServices.CreateUpdateDesignFile({
                    id: 0,
                    designId: 0,
                    designSizeId: row.sizeMm,
                    fileFormat: "png",
                    fileUrl: row.pngUrl,
                    fileName: row.pngFileName,
                    label: pattern.name,
                    boundingWidthMm: row.sizeMm,
                });
                if (err) hasError = true;
            }
        }

        setIsBusy(false);
        if (!hasError) {
            Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Lưu thành công");
            props.saveSucceed();
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Lưu thất bại");
        }
    }

    return (
        <CommonModal show={props.isShowModal} id={Common.generateGuid()} size='xl' isBusy={isBusy} busyMsg='Đang xử lý...' title='Thêm họa tiết thêu' onSave={_handleSave} onClose={() => props.onClose()}>
            <div className='form-group'>
                <label htmlFor='name' className='required'>
                    Tên họa tiết thêu
                </label>
                <Textbox id='name' name='name' required ref={addRef} className='form-control' onChanged={(name, value) => setPattern({ ...pattern, name: value })} value={pattern.name} />
            </div>
            <div className='form-group'>
                <label htmlFor='tag'>Tag</label>
                <Textbox id='tag' name='tag' className='form-control' onChanged={(name, value) => setPattern({ ...pattern, tag: value })} value={pattern.tag} />
            </div>
            <div className='form-group'>
                <label>
                    File .EBM <span className='text-muted'>(File thiết kế Wilcom gốc)</span>
                </label>
                <div className='d-flex align-items-center'>
                    <input type='file' id='ebm-file' className='d-none' onChange={_handleUploadEbm} />
                    <button className='btn btn-outline-secondary' onClick={() => document.getElementById("ebm-file").click()}>
                        Choose file
                    </button>
                    {pattern.ebmFileName && <span className='ml-2'>{pattern.ebmFileName}</span>}
                </div>
            </div>

            <label>File theo kích thước</label>
            <table className='table table-bordered'>
                <thead>
                    <tr className='text-center'>
                        <th>Kích thước</th>
                        <th>File .DST</th>
                        <th>File .PNG (hiển thị tên web)</th>
                        <th>Preview</th>
                        <th>Hành động</th>
                    </tr>
                </thead>
                <tbody>
                    {sizeRows.map((row, idx) => (
                        <tr key={idx}>
                            <td className='text-center'>{row.sizeMm}mm</td>
                            <td className='text-center'>
                                <input type='file' id={`dst-file-${idx}`} className='d-none' onChange={(e) => _handleUploadDst(idx, e)} />
                                <button className='btn btn-outline-secondary btn-sm' onClick={() => document.getElementById(`dst-file-${idx}`).click()}>
                                    Choose file
                                </button>
                                <div>{row.dstFileName || "Chưa upload"}</div>
                            </td>
                            <td className='text-center'>
                                <input type='file' id={`png-file-${idx}`} className='d-none' accept='image/png' onChange={(e) => _handleUploadPng(idx, e)} />
                                <button className='btn btn-outline-secondary btn-sm' onClick={() => document.getElementById(`png-file-${idx}`).click()}>
                                    Choose file
                                </button>
                                <div>{row.pngFileName || "Chưa upload"}</div>
                            </td>
                            <td className='text-center'>{row.pngUrl ? <img src={row.pngUrl} alt='' style={{ width: 50, height: 50, objectFit: "contain" }} /> : "-"}</td>
                            <td className='text-center'>
                                <button className='btn btn-danger btn-sm' onClick={() => _handleRemoveRow(idx)}>
                                    Xóa
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </CommonModal>
    );
}

PatternUploadModal.propTypes = {
    id: PropTypes.number,
    isShowModal: PropTypes.bool,
    onClose: PropTypes.func,
    saveSucceed: PropTypes.func,
};

export default PatternUploadModal;
